import React, { useState, useEffect } from 'react';
import { X, Check, User, Palette, Shirt, Sparkles, Shuffle, Eye, Smile } from 'lucide-react';
import { avatarAPI } from '../api';
import { toast } from 'sonner';

// Avatar preview component - renders the avatar with optional animations
export const AvatarPreview = ({ 
  avatar, 
  size = 'md', 
  showBadge = false, 
  type = 'explorer', 
  showAura = false, 
  auraColor = '#818CF8', 
  showBreathing = false, 
  showSparkles = false,
  showBlinking = false,
  showCelebration = false,
  isIdle = false // When true, enables subtle idle animations (breathing + blinking)
}) => {
  const sizes = {
    sm: { container: 'w-8 h-8', eyes: 'w-1 h-1', eyeGap: 'gap-1', smile: 'w-1.5', mt: 'mt-1', accSize: 'text-[10px]', accPos: 'top-0 -right-1' },
    md: { container: 'w-12 h-12', eyes: 'w-1 h-1', eyeGap: 'gap-1.5', smile: 'w-2', mt: 'mt-1.5', accSize: 'text-sm', accPos: '-top-1 -right-1' },
    lg: { container: 'w-24 h-24', eyes: 'w-2 h-2', eyeGap: 'gap-3', smile: 'w-4', mt: 'mt-3', accSize: 'text-lg', accPos: '-top-2 -right-2' },
    xl: { container: 'w-32 h-32', eyes: 'w-2.5 h-2.5', eyeGap: 'gap-4', smile: 'w-5', mt: 'mt-4', accSize: 'text-xl', accPos: '-top-2 -right-2' }
  };
  
  const sizeConfig = sizes[size] || sizes.md;
  
  // Enable idle animations when isIdle is true
  const enableBreathing = showBreathing || isIdle;
  const enableBlinking = showBlinking || isIdle;
  
  const outfitColors = {
    explorer_tunic: '#818CF8',
    star_robe: '#FBBF24',
    forest_cloak: '#22C55E',
    ocean_vest: '#06B6D4',
    fire_jacket: '#EF4444',
    sky_cape: '#8B5CF6'
  };
  
  const outfitColor = outfitColors[avatar?.outfit] || '#818CF8';
  const hairColor = avatar?.hair_color || '#2C1810';
  const skinColor = avatar?.skin_tone || '#F5D0C5';
  const hairStyle = avatar?.hair_style || 'medium';
  
  // Accessory emoji and anchor point system
  const getAccessoryConfig = (accessory) => {
    // Improved anchor points with better positioning for face alignment
    // All positions are relative to the avatar container
    const anchorPoints = {
      head_anchor: { top: '-8%', left: '50%', transform: 'translateX(-50%)', zIndex: 20 },
      face_anchor: { top: '36%', left: '50%', transform: 'translateX(-50%) scale(0.85)', zIndex: 15 },
      ear_left_anchor: { top: '38%', left: '5%', transform: 'scale(0.7)', zIndex: 10 },
      ear_right_anchor: { top: '38%', right: '5%', transform: 'scale(0.7)', zIndex: 10 },
      neck_anchor: { top: '68%', left: '50%', transform: 'translateX(-50%) scale(0.8)', zIndex: 5 },
      hair_anchor: { top: '8%', right: '12%', transform: 'scale(0.7)', zIndex: 25 },
      forehead_anchor: { top: '15%', left: '50%', transform: 'translateX(-50%) scale(0.75)', zIndex: 18 },
      back_anchor: { top: '25%', left: '50%', transform: 'translateX(-50%) scale(0.9)', zIndex: 3 },
      above_head: { top: '-15%', left: '50%', transform: 'translateX(-50%)', zIndex: 30 }
    };
    
    // Accessory configuration with emoji, anchor, and optional size adjustment
    const accessoryConfigs = {
      none: { emoji: '', anchor: null },
      // Face accessories - scaled to fit face width
      glasses: { emoji: '👓', anchor: 'face_anchor' },
      round_glasses: { emoji: '🤓', anchor: 'face_anchor' },
      sport_glasses: { emoji: '🕶️', anchor: 'face_anchor' },
      goggles: { emoji: '🥽', anchor: 'face_anchor' },
      star_glasses: { emoji: '⭐', anchor: 'face_anchor' },
      // Head accessories - positioned on top of hair
      crown: { emoji: '👑', anchor: 'head_anchor' },
      tiara: { emoji: '👸', anchor: 'head_anchor' },
      wizard_hat: { emoji: '🧙', anchor: 'head_anchor' },
      beanie: { emoji: '🧢', anchor: 'head_anchor' },
      cap: { emoji: '🧢', anchor: 'head_anchor' },
      headband: { emoji: '🎗️', anchor: 'forehead_anchor' },
      bandana: { emoji: '🏴‍☠️', anchor: 'head_anchor' },
      pirate_hat: { emoji: '🏴‍☠️', anchor: 'head_anchor' },
      cat_ears: { emoji: '🐱', anchor: 'head_anchor' },
      bunny_ears: { emoji: '🐰', anchor: 'head_anchor' },
      antenna: { emoji: '📡', anchor: 'above_head' },
      // Ear accessories - positioned at ear level
      earrings: { emoji: '💎', anchor: 'ear_left_anchor' },
      beads: { emoji: '📿', anchor: 'ear_left_anchor' },
      pendant: { emoji: '💫', anchor: 'neck_anchor' },
      star_pendant: { emoji: '⭐', anchor: 'neck_anchor' },
      // Neck accessories
      necklace: { emoji: '📿', anchor: 'neck_anchor' },
      scarf: { emoji: '🧣', anchor: 'neck_anchor' },
      cape: { emoji: '🦸', anchor: 'neck_anchor' },
      backpack: { emoji: '🎒', anchor: 'neck_anchor' },
      // Hair accessories - positioned in hair area
      hairclips: { emoji: '🎀', anchor: 'hair_anchor' },
      hair_ribbon: { emoji: '🎀', anchor: 'hair_anchor' },
      flower: { emoji: '🌸', anchor: 'hair_anchor' },
      feather: { emoji: '🪶', anchor: 'hair_anchor' },
      bow: { emoji: '🎀', anchor: 'hair_anchor' },
      sparkle_clips: { emoji: '✨', anchor: 'hair_anchor' },
      // Audio
      headphones: { emoji: '🎧', anchor: 'head_anchor' },
      // Special
      wings: { emoji: '🦋', anchor: 'back_anchor' },
      halo: { emoji: '😇', anchor: 'above_head' }
    };
    
    const config = accessoryConfigs[accessory] || accessoryConfigs.none;
    return {
      emoji: config.emoji,
      position: config.anchor ? anchorPoints[config.anchor] : null
    };
  };
  
  // Legacy function for compatibility
  const getAccessoryEmoji = (accessory) => getAccessoryConfig(accessory).emoji;
  
  // Hair height based on style
  const hairHeights = {
    short: '20%', medium: '28%', long: '38%', curly: '30%',
    wavy: '32%', coily: '28%', afro: '35%', locs: '30%',
    twists: '28%', mohawk: '25%', buzzcut: '15%', fade: '18%',
    spiky: '22%', ponytail: '26%', braids: '28%', buns: '20%',
    pigtails: '24%', cornrows: '22%', straight: '30%',
    high_ponytail: '18%', low_ponytail: '28%', double_buns: '15%',
    top_knot: '15%', side_braid: '28%', french_braid: '26%',
    space_buns: '15%', box_braids: '28%', undercut: '18%'
  };
  
  return (
    <div className={`relative ${sizeConfig.container} ${showCelebration ? 'animate-celebration-wiggle' : ''}`}>
      {/* Milestone Aura Effect */}
      {showAura && (
        <>
          <div 
            className="absolute -inset-2 rounded-full animate-pulse-slow opacity-50"
            style={{ 
              background: `radial-gradient(circle, ${auraColor}40 0%, transparent 70%)`,
              filter: 'blur(8px)'
            }}
          />
          <div 
            className="absolute -inset-1 rounded-full animate-pulse-slower"
            style={{ 
              background: `radial-gradient(circle, ${auraColor}30 0%, transparent 60%)`,
              filter: 'blur(4px)'
            }}
          />
        </>
      )}
      
      {/* Celebration Glow Effect */}
      {showCelebration && (
        <div className="absolute -inset-1 rounded-full animate-celebration-glow" />
      )}
      
      {/* Celebration Sparkles - Enhanced floating particles */}
      {(showSparkles || showCelebration) && (
        <div className="absolute -inset-4 pointer-events-none overflow-visible">
          {[...Array(showCelebration ? 10 : 6)].map((_, i) => (
            <div
              key={i}
              className={`absolute rounded-full ${showCelebration ? 'animate-celebration-float' : 'animate-sparkle'}`}
              style={{
                left: `${10 + Math.random() * 80}%`,
                top: `${10 + Math.random() * 80}%`,
                animationDelay: `${i * 0.15}s`,
                width: showCelebration ? '6px' : '4px',
                height: showCelebration ? '6px' : '4px',
                backgroundColor: ['#FBBF24', '#F59E0B', '#FCD34D', '#FBBF24', '#EAB308'][i % 5]
              }}
            />
          ))}
          {/* Star sparkles for celebration */}
          {showCelebration && [...Array(4)].map((_, i) => (
            <div
              key={`star-${i}`}
              className="absolute text-amber-400 animate-celebration-float"
              style={{
                left: `${20 + i * 20}%`,
                top: `${-10 + Math.random() * 30}%`,
                animationDelay: `${0.3 + i * 0.2}s`,
                fontSize: '12px'
              }}
            >
              ✨
            </div>
          ))}
        </div>
      )}
      
      {/* Main avatar circle with breathing animation */}
      <div 
        className={`absolute inset-0 rounded-full overflow-hidden ${enableBreathing ? 'animate-avatar-breathe' : ''}`}
      >
        {/* Background with outfit color */}
        <div 
          className="absolute inset-0 rounded-full"
          style={{ 
            background: `linear-gradient(135deg, ${outfitColor}40 0%, ${outfitColor}20 100%)`,
            border: `2px solid ${outfitColor}60`
          }}
        />
        
        {/* Skin tone (face) */}
        <div 
          className="absolute inset-[15%] rounded-full"
          style={{ backgroundColor: skinColor }}
        />
        
        {/* Base hair */}
        <div 
          className="absolute top-0 left-0 right-0 rounded-t-full"
          style={{ backgroundColor: hairColor, height: hairHeights[hairStyle] || '28%' }}
        />
      </div>
      
      {/* Extended hair elements (outside overflow:hidden) */}
      {hairStyle === 'curly' && (
        <>
          <div className="absolute rounded-full" style={{ 
            backgroundColor: hairColor, top: '-5%', left: '15%', width: '22%', height: '22%' }} />
          <div className="absolute rounded-full" style={{ 
            backgroundColor: hairColor, top: '-8%', left: '35%', width: '25%', height: '25%' }} />
          <div className="absolute rounded-full" style={{ 
            backgroundColor: hairColor, top: '-5%', right: '15%', width: '22%', height: '22%' }} />
        </>
      )}
      {hairStyle === 'wavy' && (
        <>
          <div className="absolute rounded-full" style={{ 
            backgroundColor: hairColor, top: '-3%', left: '18%', width: '18%', height: '18%' }} />
          <div className="absolute rounded-full" style={{ 
            backgroundColor: hairColor, top: '-5%', left: '40%', width: '20%', height: '18%' }} />
          <div className="absolute rounded-full" style={{ 
            backgroundColor: hairColor, top: '-3%', right: '18%', width: '18%', height: '18%' }} />
        </>
      )}
      {hairStyle === 'coily' && (
        <>
          <div className="absolute rounded-full" style={{ 
            backgroundColor: hairColor, top: '-6%', left: '12%', width: '20%', height: '20%' }} />
          <div className="absolute rounded-full" style={{ 
            backgroundColor: hairColor, top: '-10%', left: '32%', width: '22%', height: '22%' }} />
          <div className="absolute rounded-full" style={{ 
            backgroundColor: hairColor, top: '-10%', right: '32%', width: '22%', height: '22%' }} />
          <div className="absolute rounded-full" style={{ 
            backgroundColor: hairColor, top: '-6%', right: '12%', width: '20%', height: '20%' }} />
        </>
      )}
      {hairStyle === 'afro' && (
        <>
          <div className="absolute rounded-full" style={{ 
            backgroundColor: hairColor, top: '-15%', left: '10%', width: '80%', height: '50%' }} />
          <div className="absolute rounded-full" style={{ 
            backgroundColor: hairColor, top: '5%', left: '-5%', width: '25%', height: '40%' }} />
          <div className="absolute rounded-full" style={{ 
            backgroundColor: hairColor, top: '5%', right: '-5%', width: '25%', height: '40%' }} />
        </>
      )}
      {hairStyle === 'locs' && (
        <>
          <div className="absolute rounded-full" style={{ 
            backgroundColor: hairColor, top: '15%', left: '-8%', width: '20%', height: '50%' }} />
          <div className="absolute rounded-full" style={{ 
            backgroundColor: hairColor, top: '15%', right: '-8%', width: '20%', height: '50%' }} />
          <div className="absolute rounded-full" style={{ 
            backgroundColor: hairColor, top: '25%', left: '5%', width: '12%', height: '45%' }} />
          <div className="absolute rounded-full" style={{ 
            backgroundColor: hairColor, top: '25%', right: '5%', width: '12%', height: '45%' }} />
        </>
      )}
      {hairStyle === 'twists' && (
        <>
          <div className="absolute" style={{ 
            backgroundColor: hairColor, top: '20%', left: '-5%', width: '15%', height: '40%',
            borderRadius: '50%' }} />
          <div className="absolute" style={{ 
            backgroundColor: hairColor, top: '20%', right: '-5%', width: '15%', height: '40%',
            borderRadius: '50%' }} />
        </>
      )}
      {hairStyle === 'mohawk' && (
        <>
          <div className="absolute" style={{ 
            backgroundColor: hairColor, top: '-25%', left: '35%', width: '30%', height: '35%',
            clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)', borderRadius: '30% 30% 0 0' }} />
          <div className="absolute" style={{ 
            backgroundColor: hairColor, top: '-10%', left: '38%', width: '24%', height: '22%',
            borderRadius: '50% 50% 0 0' }} />
        </>
      )}
      {hairStyle === 'fade' && (
        <>
          <div className="absolute" style={{ 
            backgroundColor: hairColor, top: '5%', left: '15%', width: '70%', height: '18%',
            opacity: 0.7, borderRadius: '40% 40% 0 0' }} />
        </>
      )}
      {hairStyle === 'spiky' && (
        <>
          <div className="absolute" style={{ 
            backgroundColor: hairColor, top: '-12%', left: '18%', width: '18%', height: '22%',
            clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }} />
          <div className="absolute" style={{ 
            backgroundColor: hairColor, top: '-18%', left: '38%', width: '22%', height: '28%',
            clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }} />
          <div className="absolute" style={{ 
            backgroundColor: hairColor, top: '-12%', right: '18%', width: '18%', height: '22%',
            clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }} />
        </>
      )}
      {hairStyle === 'ponytail' && (
        <div className="absolute rounded-full" style={{ 
          backgroundColor: hairColor, top: '8%', right: '-8%', width: '25%', height: '40%' }} />
      )}
      {hairStyle === 'braids' && (
        <>
          <div className="absolute rounded-full" style={{ 
            backgroundColor: hairColor, top: '28%', left: '-5%', width: '15%', height: '45%' }} />
          <div className="absolute rounded-full" style={{ 
            backgroundColor: hairColor, top: '28%', right: '-5%', width: '15%', height: '45%' }} />
        </>
      )}
      {hairStyle === 'buns' && (
        <>
          <div className="absolute rounded-full" style={{ 
            backgroundColor: hairColor, top: '-12%', left: '8%', width: '30%', height: '30%' }} />
          <div className="absolute rounded-full" style={{ 
            backgroundColor: hairColor, top: '-12%', right: '8%', width: '30%', height: '30%' }} />
        </>
      )}
      {hairStyle === 'pigtails' && (
        <>
          <div className="absolute rounded-full" style={{ 
            backgroundColor: hairColor, top: '15%', left: '-10%', width: '22%', height: '35%' }} />
          <div className="absolute rounded-full" style={{ 
            backgroundColor: hairColor, top: '15%', right: '-10%', width: '22%', height: '35%' }} />
        </>
      )}
      {hairStyle === 'cornrows' && (
        <>
          <div className="absolute" style={{ 
            backgroundColor: hairColor, top: '5%', left: '20%', width: '8%', height: '30%',
            borderRadius: '20%' }} />
          <div className="absolute" style={{ 
            backgroundColor: hairColor, top: '5%', left: '35%', width: '8%', height: '30%',
            borderRadius: '20%' }} />
          <div className="absolute" style={{ 
            backgroundColor: hairColor, top: '5%', right: '35%', width: '8%', height: '30%',
            borderRadius: '20%' }} />
          <div className="absolute" style={{ 
            backgroundColor: hairColor, top: '5%', right: '20%', width: '8%', height: '30%',
            borderRadius: '20%' }} />
        </>
      )}
      {hairStyle === 'long' && (
        <>
          <div className="absolute rounded-full" style={{ 
            backgroundColor: hairColor, top: '32%', left: '-3%', width: '18%', height: '35%', opacity: 0.9 }} />
          <div className="absolute rounded-full" style={{ 
            backgroundColor: hairColor, top: '32%', right: '-3%', width: '18%', height: '35%', opacity: 0.9 }} />
        </>
      )}
      {/* Short - simple small cap */}
      {hairStyle === 'short' && (
        <div className="absolute rounded-t-full" style={{ 
          backgroundColor: hairColor, top: '5%', left: '18%', width: '64%', height: '18%' }} />
      )}
      {/* Medium - slightly fuller */}
      {hairStyle === 'medium' && (
        <>
          <div className="absolute rounded-full" style={{ 
            backgroundColor: hairColor, top: '8%', left: '10%', width: '80%', height: '25%' }} />
        </>
      )}
      {/* Buzzcut - very short, subtle */}
      {hairStyle === 'buzzcut' && (
        <div className="absolute rounded-t-full" style={{ 
          backgroundColor: hairColor, top: '8%', left: '20%', width: '60%', height: '12%', opacity: 0.8 }} />
      )}
      {/* Straight - smooth flat look */}
      {hairStyle === 'straight' && (
        <>
          <div className="absolute" style={{ 
            backgroundColor: hairColor, top: '5%', left: '15%', width: '70%', height: '25%',
            borderRadius: '40% 40% 0 0' }} />
          <div className="absolute" style={{ 
            backgroundColor: hairColor, top: '28%', left: '-2%', width: '15%', height: '20%',
            borderRadius: '0 0 20% 40%' }} />
          <div className="absolute" style={{ 
            backgroundColor: hairColor, top: '28%', right: '-2%', width: '15%', height: '20%',
            borderRadius: '0 0 40% 20%' }} />
        </>
      )}
      {/* Box Braids - multiple braids */}
      {hairStyle === 'box_braids' && (
        <>
          <div className="absolute rounded-full" style={{ backgroundColor: hairColor, top: '20%', left: '-8%', width: '18%', height: '45%' }} />
          <div className="absolute rounded-full" style={{ backgroundColor: hairColor, top: '25%', left: '3%', width: '12%', height: '40%' }} />
          <div className="absolute rounded-full" style={{ backgroundColor: hairColor, top: '20%', right: '-8%', width: '18%', height: '45%' }} />
          <div className="absolute rounded-full" style={{ backgroundColor: hairColor, top: '25%', right: '3%', width: '12%', height: '40%' }} />
        </>
      )}
      {/* Undercut - short sides, volume on top */}
      {hairStyle === 'undercut' && (
        <>
          <div className="absolute" style={{ 
            backgroundColor: hairColor, top: '-8%', left: '25%', width: '50%', height: '30%',
            borderRadius: '50% 50% 0 0' }} />
          <div className="absolute" style={{ 
            backgroundColor: hairColor, top: '15%', left: '15%', width: '70%', height: '8%',
            opacity: 0.5 }} />
        </>
      )}
      {/* Wild - chaotic spikes everywhere */}
      {hairStyle === 'wild' && (
        <>
          <div className="absolute" style={{ backgroundColor: hairColor, top: '-18%', left: '20%', width: '15%', height: '25%', clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }} />
          <div className="absolute" style={{ backgroundColor: hairColor, top: '-22%', left: '35%', width: '18%', height: '30%', clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }} />
          <div className="absolute" style={{ backgroundColor: hairColor, top: '-15%', right: '22%', width: '14%', height: '22%', clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }} />
          <div className="absolute" style={{ backgroundColor: hairColor, top: '-10%', left: '10%', width: '12%', height: '18%', clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)', transform: 'rotate(-20deg)' }} />
          <div className="absolute" style={{ backgroundColor: hairColor, top: '-10%', right: '12%', width: '12%', height: '18%', clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)', transform: 'rotate(20deg)' }} />
        </>
      )}
      {/* High Ponytail - on top of head */}
      {hairStyle === 'high_ponytail' && (
        <>
          <div className="absolute rounded-full" style={{ backgroundColor: hairColor, top: '-18%', left: '35%', width: '30%', height: '35%' }} />
          <div className="absolute" style={{ backgroundColor: hairColor, top: '8%', left: '40%', width: '20%', height: '10%', borderRadius: '50%' }} />
        </>
      )}
      {/* Low Ponytail - at back of neck */}
      {hairStyle === 'low_ponytail' && (
        <div className="absolute rounded-full" style={{ backgroundColor: hairColor, top: '45%', left: '40%', width: '20%', height: '30%' }} />
      )}
      {/* Double Buns - two buns on top */}
      {hairStyle === 'double_buns' && (
        <>
          <div className="absolute rounded-full" style={{ backgroundColor: hairColor, top: '-15%', left: '12%', width: '28%', height: '28%' }} />
          <div className="absolute rounded-full" style={{ backgroundColor: hairColor, top: '-15%', right: '12%', width: '28%', height: '28%' }} />
        </>
      )}
      {/* Top Knot - single bun on top */}
      {hairStyle === 'top_knot' && (
        <div className="absolute rounded-full" style={{ backgroundColor: hairColor, top: '-20%', left: '30%', width: '40%', height: '35%' }} />
      )}
      {/* Side Braid - braid to one side */}
      {hairStyle === 'side_braid' && (
        <>
          <div className="absolute rounded-full" style={{ backgroundColor: hairColor, top: '20%', left: '-12%', width: '22%', height: '50%' }} />
          <div className="absolute rounded-full" style={{ backgroundColor: hairColor, top: '10%', left: '-5%', width: '15%', height: '15%' }} />
        </>
      )}
      {/* French Braid - braided back */}
      {hairStyle === 'french_braid' && (
        <>
          <div className="absolute" style={{ backgroundColor: hairColor, top: '5%', left: '25%', width: '50%', height: '20%', borderRadius: '50% 50% 0 0' }} />
          <div className="absolute" style={{ backgroundColor: hairColor, top: '20%', left: '38%', width: '24%', height: '50%', borderRadius: '30%' }} />
        </>
      )}
      {/* Space Buns - two buns with extra height */}
      {hairStyle === 'space_buns' && (
        <>
          <div className="absolute rounded-full" style={{ backgroundColor: hairColor, top: '-20%', left: '8%', width: '32%', height: '32%' }} />
          <div className="absolute rounded-full" style={{ backgroundColor: hairColor, top: '-20%', right: '8%', width: '32%', height: '32%' }} />
        </>
      )}
      {/* Messy - tousled look */}
      {hairStyle === 'messy' && (
        <>
          <div className="absolute rounded-full" style={{ backgroundColor: hairColor, top: '-8%', left: '12%', width: '25%', height: '25%' }} />
          <div className="absolute rounded-full" style={{ backgroundColor: hairColor, top: '-12%', left: '30%', width: '30%', height: '28%' }} />
          <div className="absolute rounded-full" style={{ backgroundColor: hairColor, top: '-6%', right: '15%', width: '22%', height: '22%' }} />
          <div className="absolute rounded-full" style={{ backgroundColor: hairColor, top: '5%', left: '5%', width: '15%', height: '18%' }} />
          <div className="absolute rounded-full" style={{ backgroundColor: hairColor, top: '5%', right: '5%', width: '15%', height: '18%' }} />
        </>
      )}
      {/* Swoosh - side-swept look */}
      {hairStyle === 'swoosh' && (
        <>
          <div className="absolute" style={{ 
            backgroundColor: hairColor, top: '-5%', left: '10%', width: '80%', height: '25%',
            borderRadius: '60% 30% 0 0', transform: 'skewX(-10deg)' }} />
        </>
      )}
      {/* Fluffy - soft voluminous look */}
      {hairStyle === 'fluffy' && (
        <>
          <div className="absolute rounded-full" style={{ backgroundColor: hairColor, top: '-12%', left: '8%', width: '85%', height: '40%', opacity: 0.9 }} />
          <div className="absolute rounded-full" style={{ backgroundColor: hairColor, top: '5%', left: '-2%', width: '20%', height: '25%', opacity: 0.8 }} />
          <div className="absolute rounded-full" style={{ backgroundColor: hairColor, top: '5%', right: '-2%', width: '20%', height: '25%', opacity: 0.8 }} />
        </>
      )}
      {/* Poofy - big round volume */}
      {hairStyle === 'poofy' && (
        <>
          <div className="absolute rounded-full" style={{ backgroundColor: hairColor, top: '-18%', left: '5%', width: '90%', height: '55%' }} />
          <div className="absolute rounded-full" style={{ backgroundColor: hairColor, top: '8%', left: '-5%', width: '25%', height: '30%' }} />
          <div className="absolute rounded-full" style={{ backgroundColor: hairColor, top: '8%', right: '-5%', width: '25%', height: '30%' }} />
        </>
      )}
      
      {/* Face features with customization */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className={`relative ${sizeConfig.mt}`}>
          {/* Eyes - shaped based on eye_shape option with optional blinking */}
          <div className={`flex ${sizeConfig.eyeGap} mb-1 ${enableBlinking ? 'animate-avatar-blink' : ''}`} style={{ transformOrigin: 'center' }}>
            {(() => {
              const eyeColor = avatar?.eye_color || '#4A3728';
              const eyeShape = avatar?.eye_shape || 'round';
              
              // Eye shape styles
              const shapeStyles = {
                round: 'rounded-full aspect-square',
                almond: 'rounded-full scale-x-125',
                wide: 'rounded-full scale-x-110 scale-y-90',
                narrow: 'rounded-full scale-x-75 scale-y-110',
                upturned: 'rounded-full -rotate-6',
                downturned: 'rounded-full rotate-6',
                hooded: 'rounded-full scale-y-75',
                sparkle: 'rounded-full aspect-square'
              };
              
              const shapeClass = shapeStyles[eyeShape] || shapeStyles.round;
              const isSparkle = eyeShape === 'sparkle';
              
              return (
                <>
                  <div className={`${sizeConfig.eyes} ${shapeClass} relative`} style={{ backgroundColor: eyeColor }}>
                    <div className="absolute bg-white rounded-full opacity-60" style={{ top: '15%', left: '15%', width: '30%', height: '30%' }} />
                    {isSparkle && <div className="absolute text-white" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '60%' }}>✨</div>}
                  </div>
                  <div className={`${sizeConfig.eyes} ${shapeClass} relative`} style={{ backgroundColor: eyeColor }}>
                    <div className="absolute bg-white rounded-full opacity-60" style={{ top: '15%', left: '15%', width: '30%', height: '30%' }} />
                    {isSparkle && <div className="absolute text-white" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '60%' }}>✨</div>}
                  </div>
                </>
              );
            })()}
          </div>
          
          {/* Smile - styled based on smile option */}
          {(() => {
            const smileType = avatar?.smile || 'happy';
            const smileStyles = {
              happy: { borderRadius: '0 0 8px 8px', height: '3px', backgroundColor: '#78350f' },
              gentle: { borderRadius: '0 0 4px 4px', height: '2px', backgroundColor: '#78350f' },
              excited: { borderRadius: '0 0 12px 12px', height: '5px', backgroundColor: '#78350f' },
              calm: { borderRadius: '2px', height: '2px', backgroundColor: '#78350f' },
              confident: { borderRadius: '0 0 6px 6px', height: '3px', backgroundColor: '#78350f', transform: 'scaleX(1.2)' },
              silly: { borderRadius: '50%', height: '4px', backgroundColor: '#78350f', transform: 'rotate(-5deg)' },
              big_grin: { borderRadius: '0 0 14px 14px', height: '6px', backgroundColor: '#78350f', transform: 'scaleX(1.3)' },
              smirk: { borderRadius: '0 0 4px 8px', height: '2px', backgroundColor: '#78350f', transform: 'skewX(-8deg)' }
            };
            const style = smileStyles[smileType] || smileStyles.happy;
            return <div className={`${sizeConfig.smile} mx-auto`} style={style}></div>;
          })()}
        </div>
      </div>
      
      {/* Face features overlay (freckles, blush, etc.) */}
      {avatar?.face_feature && avatar.face_feature !== 'none' && (
        <div className="absolute inset-0 pointer-events-none">
          {avatar.face_feature === 'freckles' && (
            <>
              <div className="absolute bg-amber-800/40 rounded-full" style={{ top: '42%', left: '28%', width: '3%', height: '3%' }} />
              <div className="absolute bg-amber-800/40 rounded-full" style={{ top: '45%', left: '32%', width: '2%', height: '2%' }} />
              <div className="absolute bg-amber-800/40 rounded-full" style={{ top: '44%', left: '38%', width: '2%', height: '2%' }} />
              <div className="absolute bg-amber-800/40 rounded-full" style={{ top: '42%', right: '28%', width: '3%', height: '3%' }} />
              <div className="absolute bg-amber-800/40 rounded-full" style={{ top: '45%', right: '32%', width: '2%', height: '2%' }} />
              <div className="absolute bg-amber-800/40 rounded-full" style={{ top: '44%', right: '38%', width: '2%', height: '2%' }} />
            </>
          )}
          {avatar.face_feature === 'light_freckles' && (
            <>
              <div className="absolute bg-amber-800/25 rounded-full" style={{ top: '43%', left: '30%', width: '2%', height: '2%' }} />
              <div className="absolute bg-amber-800/25 rounded-full" style={{ top: '43%', right: '30%', width: '2%', height: '2%' }} />
            </>
          )}
          {avatar.face_feature === 'dimples' && (
            <>
              <div className="absolute bg-pink-800/20 rounded-full" style={{ top: '52%', left: '25%', width: '4%', height: '3%' }} />
              <div className="absolute bg-pink-800/20 rounded-full" style={{ top: '52%', right: '25%', width: '4%', height: '3%' }} />
            </>
          )}
          {avatar.face_feature === 'beauty_mark' && (
            <div className="absolute bg-amber-900/60 rounded-full" style={{ top: '48%', right: '32%', width: '2%', height: '2%' }} />
          )}
          {avatar.face_feature === 'blush' && (
            <>
              <div className="absolute bg-pink-400/30 rounded-full" style={{ top: '45%', left: '22%', width: '12%', height: '8%', filter: 'blur(2px)' }} />
              <div className="absolute bg-pink-400/30 rounded-full" style={{ top: '45%', right: '22%', width: '12%', height: '8%', filter: 'blur(2px)' }} />
            </>
          )}
          {avatar.face_feature === 'rosy_cheeks' && (
            <>
              <div className="absolute bg-rose-500/40 rounded-full" style={{ top: '42%', left: '20%', width: '14%', height: '10%', filter: 'blur(3px)' }} />
              <div className="absolute bg-rose-500/40 rounded-full" style={{ top: '42%', right: '20%', width: '14%', height: '10%', filter: 'blur(3px)' }} />
            </>
          )}
          {avatar.face_feature === 'star_marks' && (
            <>
              <div className="absolute text-amber-400/60" style={{ top: '35%', left: '15%', fontSize: '30%' }}>⭐</div>
              <div className="absolute text-amber-400/60" style={{ top: '38%', right: '15%', fontSize: '25%' }}>✨</div>
            </>
          )}
        </div>
      )}
      
      {/* Accessory - positioned using anchor point system */}
      {avatar?.accessory && avatar.accessory !== 'none' && (() => {
        const accessoryConfig = getAccessoryConfig(avatar.accessory);
        if (!accessoryConfig.position) return null;
        return (
          <div 
            className={`absolute z-10 ${sizeConfig.accSize}`}
            style={accessoryConfig.position}
          >
            {accessoryConfig.emoji}
          </div>
        );
      })()}
      
      {/* Type badge */}
      {showBadge && (
        <div 
          className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-1 py-0.5 rounded text-[8px] font-bold whitespace-nowrap z-10"
          style={{ backgroundColor: type === 'explorer' ? '#818CF8' : '#FBBF24', color: 'white' }}
        >
          {type === 'explorer' ? 'Explorer' : 'Guide'}
        </div>
      )}
    </div>
  );
};

// Avatar Builder Modal
export const AvatarBuilderModal = ({ 
  isOpen, 
  onClose, 
  childId = null, // null for parent avatar
  initialAvatar = null,
  onSave 
}) => {
  const [avatar, setAvatar] = useState(initialAvatar || {
    body_type: 'rounded',
    skin_tone: '#F5D0C5',
    hair_style: 'short',
    hair_color: '#2C1810',
    eye_shape: 'round',
    eye_color: '#4A3728',
    face_feature: 'none',
    smile: 'happy',
    outfit: 'explorer_tunic',
    accessory: 'none'
  });
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeCategory, setActiveCategory] = useState('skin_tone');
  
  const isExplorer = childId !== null;
  
  useEffect(() => {
    if (isOpen) {
      loadOptions();
    }
  }, [isOpen]);
  
  const loadOptions = async () => {
    try {
      const res = await avatarAPI.getOptions();
      setOptions(res.data);
      if (initialAvatar) {
        setAvatar(initialAvatar);
      } else if (res.data.default_avatar) {
        setAvatar(res.data.default_avatar);
      }
    } catch (error) {
      console.error('Error loading avatar options:', error);
      toast.error('Could not load avatar options');
    } finally {
      setLoading(false);
    }
  };
  
  // Randomize avatar function
  const randomizeAvatar = () => {
    if (!options?.options) return;
    
    const randomChoice = (arr) => arr[Math.floor(Math.random() * arr.length)];
    const opts = options.options;
    
    setAvatar({
      body_type: randomChoice(opts.body_type || ['rounded']),
      skin_tone: randomChoice(opts.skin_tone || ['#F5D0C5']),
      hair_style: randomChoice(opts.hair_style || ['short']),
      hair_color: randomChoice(opts.hair_color || ['#2C1810']),
      eye_shape: randomChoice(opts.eye_shape || ['round']),
      eye_color: randomChoice(opts.eye_color || ['#4A3728']),
      face_feature: randomChoice(opts.face_feature || ['none']),
      smile: randomChoice(opts.smile || ['happy']),
      outfit: randomChoice(opts.outfit || ['explorer_tunic']),
      accessory: randomChoice(opts.accessory || ['none'])
    });
    
    toast.success('Random explorer created! ✨');
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      if (isExplorer) {
        await avatarAPI.updateChildAvatar(childId, avatar);
      } else {
        await avatarAPI.updateParentAvatar(avatar);
      }
      toast.success('Avatar saved!');
      onSave?.(avatar);
      onClose();
    } catch (error) {
      toast.error('Could not save avatar');
    } finally {
      setSaving(false);
    }
  };
  
  const updateAvatar = (field, value) => {
    setAvatar(prev => ({ ...prev, [field]: value }));
  };
  
  const categories = [
    { id: 'skin_tone', name: 'Skin', icon: User },
    { id: 'hair_style', name: 'Hair', icon: Sparkles },
    { id: 'hair_color', name: 'Hair Color', icon: Palette },
    { id: 'eye_shape', name: 'Eyes', icon: Eye },
    { id: 'eye_color', name: 'Eye Color', icon: Palette },
    { id: 'face_feature', name: 'Face', icon: Smile },
    { id: 'smile', name: 'Smile', icon: Smile },
    { id: 'outfit', name: 'Outfit', icon: Shirt },
    { id: 'accessory', name: 'Accessory', icon: Sparkles }
  ];
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="glass-panel p-6 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-philosopher font-bold text-white">
              Create Your {isExplorer ? 'Temple Explorer' : 'Temple Guide'}
            </h2>
            <p className="text-sm text-slate-400">
              {isExplorer ? 'Design your avatar for the Temple!' : 'Customize your guide appearance'}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-white/20 border-t-indigo-500 rounded-full mx-auto"></div>
          </div>
        ) : (
          <>
            {/* Preview */}
            <div className="flex justify-center mb-4">
              <div className="relative">
                <AvatarPreview avatar={avatar} size="xl" showBadge type={isExplorer ? 'explorer' : 'guide'} />
              </div>
            </div>
            
            {/* Randomize Button */}
            <div className="flex justify-center mb-6">
              <button
                onClick={randomizeAvatar}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500/20 to-pink-500/20 hover:from-purple-500/30 hover:to-pink-500/30 border border-purple-500/30 rounded-full text-purple-300 transition-all"
                data-testid="randomize-avatar-btn"
              >
                <Shuffle className="w-4 h-4" />
                Random Explorer
              </button>
            </div>
            
            {/* Category tabs */}
            <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
              {categories.map(cat => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap transition-all ${
                      activeCategory === cat.id
                        ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                        : 'bg-slate-900/50 text-slate-400 hover:text-white'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-sm">{cat.name}</span>
                  </button>
                );
              })}
            </div>
            
            {/* Options grid */}
            <div className="bg-slate-900/50 p-4 rounded-xl mb-6">
              {activeCategory === 'skin_tone' && options?.options?.skin_tone && (
                <div className="grid grid-cols-6 gap-3">
                  {options.options.skin_tone.map(color => (
                    <button
                      key={color}
                      onClick={() => updateAvatar('skin_tone', color)}
                      className={`w-10 h-10 rounded-full transition-all ${
                        avatar.skin_tone === color ? 'ring-2 ring-white scale-110' : 'hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              )}
              
              {activeCategory === 'hair_style' && options?.options?.hair_style && (
                <div className="grid grid-cols-4 gap-3">
                  {options.options.hair_style.map(style => (
                    <button
                      key={style}
                      onClick={() => updateAvatar('hair_style', style)}
                      className={`p-3 rounded-xl text-center transition-all ${
                        avatar.hair_style === style 
                          ? 'bg-indigo-500/20 border border-indigo-500/30 text-white' 
                          : 'bg-slate-800/50 text-slate-400 hover:text-white'
                      }`}
                    >
                      <span className="text-sm capitalize">{style}</span>
                    </button>
                  ))}
                </div>
              )}
              
              {activeCategory === 'hair_color' && options?.options?.hair_color && (
                <div className="grid grid-cols-8 gap-2">
                  {options.options.hair_color.map(color => (
                    <button
                      key={color}
                      onClick={() => updateAvatar('hair_color', color)}
                      className={`w-8 h-8 rounded-full transition-all ${
                        avatar.hair_color === color ? 'ring-2 ring-white scale-110' : 'hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              )}
              
              {/* Eye Shape */}
              {activeCategory === 'eye_shape' && options?.options?.eye_shape && (
                <div className="grid grid-cols-4 gap-3">
                  {options.options.eye_shape.map(shape => (
                    <button
                      key={shape}
                      onClick={() => updateAvatar('eye_shape', shape)}
                      className={`p-3 rounded-xl text-center transition-all ${
                        avatar.eye_shape === shape 
                          ? 'bg-cyan-500/20 border border-cyan-500/30 text-cyan-300' 
                          : 'bg-slate-800/50 text-slate-400 hover:text-white'
                      }`}
                    >
                      <Eye className="w-5 h-5 mx-auto mb-1" />
                      <span className="text-xs capitalize">{shape}</span>
                    </button>
                  ))}
                </div>
              )}
              
              {/* Eye Color */}
              {activeCategory === 'eye_color' && options?.options?.eye_color && (
                <div className="grid grid-cols-6 gap-3">
                  {options.options.eye_color.map(color => (
                    <button
                      key={color}
                      onClick={() => updateAvatar('eye_color', color)}
                      className={`w-10 h-10 rounded-full transition-all border-2 ${
                        avatar.eye_color === color ? 'ring-2 ring-white scale-110' : 'hover:scale-105 border-slate-600'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              )}
              
              {/* Face Features */}
              {activeCategory === 'face_feature' && options?.options?.face_feature && (
                <div className="grid grid-cols-3 gap-3">
                  {options.options.face_feature.map(feature => (
                    <button
                      key={feature}
                      onClick={() => updateAvatar('face_feature', feature)}
                      className={`p-3 rounded-xl text-center transition-all ${
                        avatar.face_feature === feature 
                          ? 'bg-pink-500/20 border border-pink-500/30 text-pink-300' 
                          : 'bg-slate-800/50 text-slate-400 hover:text-white'
                      }`}
                    >
                      <span className="text-sm capitalize">{feature.replace('_', ' ')}</span>
                    </button>
                  ))}
                </div>
              )}
              
              {/* Smile */}
              {activeCategory === 'smile' && options?.options?.smile && (
                <div className="grid grid-cols-3 gap-3">
                  {options.options.smile.map(smileType => (
                    <button
                      key={smileType}
                      onClick={() => updateAvatar('smile', smileType)}
                      className={`p-3 rounded-xl text-center transition-all ${
                        avatar.smile === smileType 
                          ? 'bg-amber-500/20 border border-amber-500/30 text-amber-300' 
                          : 'bg-slate-800/50 text-slate-400 hover:text-white'
                      }`}
                    >
                      <Smile className="w-5 h-5 mx-auto mb-1" />
                      <span className="text-sm capitalize">{smileType}</span>
                    </button>
                  ))}
                </div>
              )}
              
              {activeCategory === 'outfit' && options?.options?.outfit && (
                <div className="grid grid-cols-3 gap-3">
                  {options.options.outfit.map(outfit => {
                    const outfitColor = options.outfit_colors?.[outfit] || '#818CF8';
                    return (
                      <button
                        key={outfit}
                        onClick={() => updateAvatar('outfit', outfit)}
                        className={`p-3 rounded-xl text-center transition-all ${
                          avatar.outfit === outfit 
                            ? 'border-2 scale-105' 
                            : 'bg-slate-800/50 hover:bg-slate-700/50'
                        }`}
                        style={avatar.outfit === outfit ? { 
                          borderColor: outfitColor,
                          backgroundColor: `${outfitColor}20`
                        } : {}}
                      >
                        <div 
                          className="w-8 h-8 rounded-lg mx-auto mb-2"
                          style={{ backgroundColor: outfitColor }}
                        />
                        <span className="text-xs text-slate-300 capitalize">
                          {outfit.replace('_', ' ')}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
              
              {activeCategory === 'accessory' && options?.options?.accessory && (
                <div className="grid grid-cols-4 gap-3">
                  {options.options.accessory.map(acc => (
                    <button
                      key={acc}
                      onClick={() => updateAvatar('accessory', acc)}
                      className={`p-3 rounded-xl text-center transition-all ${
                        avatar.accessory === acc 
                          ? 'bg-indigo-500/20 border border-indigo-500/30 text-white' 
                          : 'bg-slate-800/50 text-slate-400 hover:text-white'
                      }`}
                    >
                      <span className="text-sm capitalize">{acc}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 py-3 bg-slate-700/50 hover:bg-slate-700 text-white rounded-xl transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-3 bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-300 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                  <div className="animate-spin w-5 h-5 border-2 border-indigo-300/20 border-t-indigo-300 rounded-full"></div>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    <span>Save Avatar</span>
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AvatarBuilderModal;
